/** Size tier on every SKU the data can place. The item master tags most products Small /
 *  Medium / Large (Family where a category has it), and the tier is how a shelf row reads size,
 *  so an untagged product falls out of every size group. Studio fills a blank tier only where
 *  the tagged products around its pack size agree, and records which tiers it filled. */
import {
  SIZE_LIKE_SHARE,
  type SizeFamily,
  sizeFamilyOf,
  sizeLikeShare,
  UNKNOWN_SIZE_FAMILY,
} from "@CommonUtils/cdtStudio/domain/sizeFamily";
import { isPlaceholderValue, type StudioItem, type StudioPlacement } from "./studioTypes";

/** Live item-master columns: the tier label and the numeric pack size it summarises. */
export const SIZE_TIER_ATTR = "size_tier";
export const SIZE_ATTR = "size";
/** The tier reading order, small to large. */
export const SIZE_TIER_ORDER = ["Small", "Medium", "Large", "Family"] as const;

/** A tier's place in `SIZE_TIER_ORDER`, read off the tag's first word ("Small pack" is Small);
 *  anything else reads after every tier. */
export const sizeTierRank = (value: string): number => {
  const word = value.trim().toLowerCase().split(/\s+/)[0] ?? "";
  const index = SIZE_TIER_ORDER.findIndex((tier) => tier.toLowerCase() === word);
  return index < 0 ? SIZE_TIER_ORDER.length : index;
};

/** A tier tag as `sizeTierRank` compares it: trimmed, lower-cased. */
const normalTier = (value: string): string => value.trim().toLowerCase();

/** The spelling a filled tier takes: the `SIZE_TIER_ORDER` spelling when the tag is one of
 *  those tiers, else the tag as the item master wrote it. */
const spellingOf = (value: string): string =>
  SIZE_TIER_ORDER.find((tier) => tier.toLowerCase() === normalTier(value)) ?? value;

/** The item master's form column: pack sizes are only comparable inside one form. */
export const FORM_ATTR = "pack_format";

/** The item master's sub-brand column ("Variant / sub-brand": Original, Free & Gentle, Power
 *  PODS). Where the base lays a brand block out by it, it takes cdt2 ahead of the size tier. */
export const SUB_BRAND_ATTR = "variant_sub_brand";

/** A product's sub-brand: the item master's sub-brand column, or its flavour (`flavour`, the
 *  block's cdt3 value) where it carries none. */
export const subBrandOf = (item: StudioItem, flavour: string): string => {
  const own = String(item.attrs[SUB_BRAND_ATTR] ?? "").trim();
  return own === "" || isPlaceholderValue(own) ? flavour : own;
};

/** The unit after the number ("fl oz", "ct", "gal"), lower-cased; "" for a bare number. */
const unitOf = (item: StudioItem): string =>
  String(item.attrs[SIZE_ATTR] ?? "")
    .replace(/^\s*\d+(?:\.\d+)?/, "")
    .trim()
    .toLowerCase();

/** Products compared with each other: one form, one unit. */
const kindOf = (item: StudioItem): string =>
  `${String(item.attrs[FORM_ATTR] ?? "").trim()}␟${unitOf(item)}`;

const packSizeOf = (item: StudioItem): number | null => {
  const match = /^\s*(\d+(?:\.\d+)?)/.exec(String(item.attrs[SIZE_ATTR] ?? ""));
  const size = match ? Number(match[1]) : NaN;
  return Number.isFinite(size) && size > 0 ? size : null;
};

/** The pack size in its unit family's base unit, so "1 L" and "1000 ml" are one size. */
const measuredSizeOf = (item: StudioItem): SizeFamily | null => {
  if (packSizeOf(item) === null) return null;
  const { family, magnitude } = sizeFamilyOf(String(item.attrs[SIZE_ATTR] ?? ""));
  // Rounded so unit conversions of one size compare equal.
  return { family, magnitude: Number(magnitude.toPrecision(6)) };
};

const blankTier = (item: StudioItem): boolean => {
  const value = String(item.attrs[SIZE_TIER_ATTR] ?? "");
  return value.trim() === "" || isPlaceholderValue(value);
};

export interface SizeTierFill {
  items: StudioItem[];
  /** Products whose blank tier was filled from the tagged products around their size. */
  derived: readonly string[];
  /** Products left blank: no pack size, nothing tagged in the same form and unit, or the
   *  tags around it disagree. */
  unresolved: readonly string[];
  /** Why each unresolved product stayed blank. */
  reasons: Readonly<Record<string, string>>;
  /** Per tagged tier, the pack sizes it spans in this category: [smallest, largest]. */
  spans: Readonly<Record<string, readonly [number, number]>>;
}

/** Fills a blank `size_tier` from the tagged products of the same form and the same unit
 *  ("2 gal" is never read beside "92 fl oz", a pod count never beside a liquid): the tagged
 *  sizes nearest below and
 *  nearest above the product's pack size (equal counts as both) must all carry one tier, and
 *  the product takes it. Anything else stays blank and is reported. Tags are never changed,
 *  and no tier is made up: "Family" appears only where the data tags it. A filled tier is
 *  recorded in `derivedAttrs`. */
export const fillSizeTiers = (items: readonly StudioItem[]): SizeTierFill => {
  if (!items.some((item) => SIZE_TIER_ATTR in item.attrs))
    return { items: [...items], derived: [], unresolved: [], reasons: {}, spans: {} };
  const tagged = items
    .filter((item) => !blankTier(item))
    .flatMap((item) => {
      const size = packSizeOf(item);
      return size === null
        ? []
        : [{ size, kind: kindOf(item), tier: String(item.attrs[SIZE_TIER_ATTR] ?? "").trim() }];
    });
  const spans: Record<string, [number, number]> = {};
  for (const { size, tier } of tagged) {
    const span = spans[tier];
    spans[tier] = span ? [Math.min(span[0], size), Math.max(span[1], size)] : [size, size];
  }
  const tiersAt = (size: number, kind: string) =>
    tagged.filter((entry) => entry.size === size && entry.kind === kind).map((entry) => entry.tier);

  const derived: string[] = [];
  const unresolved: string[] = [];
  const reasons: Record<string, string> = {};
  const leave = (item: StudioItem, why: string) => {
    unresolved.push(item.skuId);
    reasons[item.skuId] = why;
    return item;
  };
  const filled = items.map((item) => {
    if (!blankTier(item)) return item;
    const size = packSizeOf(item);
    if (size === null) return leave(item, "no pack size");
    const kind = kindOf(item);
    const alike = tagged.filter((entry) => entry.kind === kind);
    const form = String(item.attrs[FORM_ATTR] ?? "").trim() || "no form";
    const unit = unitOf(item) || "no unit";
    if (alike.length === 0) return leave(item, `nothing tagged in ${form} sized in ${unit}`);
    const below = alike.filter((entry) => entry.size <= size);
    const above = alike.filter((entry) => entry.size >= size);
    if (below.length === 0 || above.length === 0)
      return leave(item, `${size} ${unit} is outside the tagged ${form} sizes`);
    const neighbours = [
      ...tiersAt(Math.max(...below.map((entry) => entry.size)), kind),
      ...tiersAt(Math.min(...above.map((entry) => entry.size)), kind),
    ];
    // Tags compare the way `sizeTierRank` reads them: "Medium" and "medium" are one tier.
    const around = new Set(neighbours.map(normalTier));
    if (around.size !== 1)
      return leave(
        item,
        `the tagged ${form} sizes around ${size} ${unit} disagree (${[...new Set(neighbours)].join(", ")})`,
      );
    derived.push(item.skuId);
    return {
      ...item,
      attrs: { ...item.attrs, [SIZE_TIER_ATTR]: spellingOf(neighbours[0]) },
      derivedAttrs: [...(item.derivedAttrs ?? []), SIZE_TIER_ATTR],
    };
  });
  return { items: filled, derived, unresolved, reasons, spans };
};

/** Where two or more pack sizes of a size-tier brand block share a shelf, the order along it:
 *  "size" (pack sizes step up left to right, flavour orders only packs of one size) or "flavour"
 *  (each flavour in one piece, its sizes inside it). */
export type WithinOrder = "size" | "flavour";

/** The brand block's order inside a shared row, read off the base. Only shelves whose products
 *  show two or more flavours and two or more pack sizes count. Each adjacent pair along such a
 *  shelf is checked against both readings: size first fits when the pack size holds or steps up,
 *  flavour first fits when the flavour holds or changes to one not yet seen on that shelf. A pair
 *  that fits only one reading votes for it. The majority wins; a tie, or no vote, is "flavour".
 *  A product with no flavour ("") or no pack size takes no part. */
export const withinOrderOf = (
  products: readonly { item: StudioItem; flavour: string }[],
  position: (placement: StudioPlacement) => number,
): WithinOrder => {
  const shelves = new Map<string, { at: number; flavour: string; size: number }[]>();
  for (const { item, flavour } of products) {
    const size = packSizeOf(item);
    if (flavour === "" || size === null) continue;
    for (const placement of item.placements) {
      const shelf = `${placement.run}/${placement.shelf}`;
      shelves.set(shelf, [
        ...(shelves.get(shelf) ?? []),
        { at: position(placement), flavour, size },
      ]);
    }
  }
  let flavourVotes = 0;
  let sizeVotes = 0;
  for (const shelf of shelves.values()) {
    if (new Set(shelf.map((p) => p.flavour)).size < 2 || new Set(shelf.map((p) => p.size)).size < 2)
      continue;
    const row = [...shelf].sort((a, b) => a.at - b.at);
    const seen = new Set([row[0].flavour]);
    for (let i = 1; i < row.length; i += 1) {
      const [left, right] = [row[i - 1], row[i]];
      const sizeFits = right.size >= left.size;
      const flavourFits = right.flavour === left.flavour || !seen.has(right.flavour);
      seen.add(right.flavour);
      if (sizeFits && !flavourFits) sizeVotes += 1;
      else if (flavourFits && !sizeFits) flavourVotes += 1;
    }
  }
  return sizeVotes > flavourVotes ? "size" : "flavour";
};

/** Every size layout question's flag id starts so (`sizeLayoutFlags`). */
export const SIZE_LAYOUT_PREFIX = "size-layout:";

/** How a brand block read Brand > [Form >] Size tier > Flavour ships: "sizeTiers", its size tiers
 *  stacked as shelf rows with the sub-brands side by side inside them (vertical blocking, each
 *  sub-brand its own column); "horizontal", horizontal blocking: the sub-brands are rows stacked
 *  top to bottom in the base's order, and inside each sub-brand row its size tiers are shelf rows,
 *  Small to Large, pack sizes left to right (it ships with the sub-brand as the block's cdt2 and
 *  the size tier as its cdt3, `collapse4`); "ladder", the base's product order kept (the
 *  merchant's answer for a block the base lays out neither way). */
export type SizeLayout = "sizeTiers" | "horizontal" | "ladder";

export interface SizeLayoutReading {
  /** The base's layout, or "unclear": two or more sub-brands, neither on rows of their own nor
   *  side by side in columns. Studio does not decide an unclear block: it asks the merchant. */
  layout: "sizeTiers" | "horizontal" | "unclear";
  /** The block's sub-brands top to bottom as the base stands them: by the first shelf each
   *  stands on, then left to right. */
  subBrands: readonly string[];
  /** Every sub-brand's pack sizes step up (or stay) left to right on every shelf. */
  sizesRunUp: boolean;
}

/** The brand block's layout, read off the base shelf by shelf, left to right.
 *
 *  - Horizontal when two or more sub-brands each stand alone on at least one shelf (rows of their
 *    own: base 210's Tide : Unit dose, Original rows and Power PODS rows), and on every shelf the
 *    size tier changes only inside one sub-brand (where one sub-brand meets the next, both packs
 *    carry the same size tier), each sub-brand's pack sizes never step down, and no two
 *    sub-brands stand side by side in the same order on two neighbouring shelves.
 *  - Else size tiers where two sub-brands stand side by side in columns: the size tier changes
 *    where they meet along a shelf (Tide : Liquid's Original 40 oz Small beside plus Febreze
 *    73 oz Medium), or they stand in the same order on two neighbouring shelves.
 *  - Else unclear: base 210's Tide Unscented, a size ladder, small to large down the block and
 *    along each shelf, Free & Gentle and Simply taking turns shelf by shelf, always at the same
 *    size tier where they meet. Studio asks (Wendy, 2026-10-23: the inference must not decide
 *    such a block silently).
 *
 *  A block of one sub-brand is size tiers: its sizes stepping up along a shelf is only where the
 *  smaller size runs out and the next begins (Tide Simply on base 210: Small on the top shelf,
 *  Medium below, Large at the bottom). The sub-brand is the item master's sub-brand column, or
 *  the flavour where a product carries none. A block with fewer than two flavours has nothing to
 *  lay out either way and keeps size tiers. No threshold: every test is an order along a shelf or
 *  between two neighbouring shelves. */
export const sizeLayoutOf = (
  products: readonly { item: StudioItem; flavour: string }[],
  position: (placement: StudioPlacement) => number,
): SizeLayoutReading => {
  const flavours = new Set(products.map(({ flavour }) => flavour).filter((f) => f !== ""));
  if (flavours.size < 2) return { layout: "sizeTiers", subBrands: [], sizesRunUp: true };
  interface Spot {
    at: number;
    subBrand: string;
    tier: string;
    size: number | null;
  }
  const runs = new Map<number, Map<number, Spot[]>>();
  /** Where each sub-brand first stands: its top shelf, then its leftmost spot there. */
  const firstAt = new Map<string, [number, number]>();
  for (const { item, flavour } of products) {
    const subBrand = subBrandOf(item, flavour);
    const tier = normalTier(String(item.attrs[SIZE_TIER_ATTR] ?? ""));
    const size = packSizeOf(item);
    for (const placement of item.placements) {
      const shelves = runs.get(placement.run) ?? new Map<number, Spot[]>();
      const shelf = shelves.get(placement.shelf) ?? [];
      const at = position(placement);
      shelf.push({ at, subBrand, tier, size });
      shelves.set(placement.shelf, shelf);
      runs.set(placement.run, shelves);
      const first = firstAt.get(subBrand);
      if (!first || placement.shelf < first[0] || (placement.shelf === first[0] && at < first[1]))
        firstAt.set(subBrand, [placement.shelf, at]);
    }
  }
  /** Where two sub-brands meet along a shelf, the size tier changes. */
  let tierChangesAtMeet = false;
  let sizesRunUp = true;
  /** Sub-brands that stand alone on at least one shelf: rows of their own. */
  const ownRows = new Set<string>();
  let sameOrder = false;
  for (const shelves of runs.values()) {
    /** Each shelf's sub-brands in the order they first stand along it. */
    const orderOn = new Map<number, string[]>();
    for (const [shelf, spots] of shelves) {
      const row = [...spots].sort((a, b) => a.at - b.at);
      const last = new Map<string, number>();
      row.forEach((spot, index) => {
        const before = row[index - 1];
        if (before && before.subBrand !== spot.subBrand && before.tier !== spot.tier)
          tierChangesAtMeet = true;
        const previous = last.get(spot.subBrand);
        if (spot.size !== null) {
          if (previous !== undefined && spot.size < previous) sizesRunUp = false;
          last.set(spot.subBrand, spot.size);
        }
      });
      const order = [...new Set(row.map((spot) => spot.subBrand))];
      if (order.length === 1) ownRows.add(order[0]);
      orderOn.set(shelf, order);
    }
    for (const [shelf, order] of orderOn) {
      const below = orderOn.get(shelf + 1);
      if (!below) continue;
      const shared = order.filter((subBrand) => below.includes(subBrand));
      // Two sub-brands side by side in the same order on neighbouring shelves: columns.
      for (let i = 1; i < shared.length; i += 1)
        if (below.indexOf(shared[i - 1]) < below.indexOf(shared[i])) sameOrder = true;
    }
  }
  const subBrands = [...firstAt.entries()]
    .sort((a, b) => a[1][0] - b[1][0] || a[1][1] - b[1][1])
    .map(([subBrand]) => subBrand);
  const horizontal = ownRows.size >= 2 && !tierChangesAtMeet && sizesRunUp && !sameOrder;
  const layout = horizontal
    ? "horizontal"
    : subBrands.length < 2 || tierChangesAtMeet || sameOrder
      ? "sizeTiers"
      : "unclear";
  return { layout, subBrands, sizesRunUp };
};

/** The unit families the wall's packs are sized in, read off every size-like column that spells
 *  a unit ("170 fl. oz.", "120 count"); bare numbers name no family. */
export const unitFamiliesOf = (items: readonly StudioItem[]): ReadonlySet<string> => {
  const attrs = [...new Set(items.flatMap((item) => Object.keys(item.attrs)))].filter(
    (attr) => sizeLikeShare(items.map((item) => item.attrs[attr] ?? null)) >= SIZE_LIKE_SHARE,
  );
  const families = new Set<string>();
  for (const item of items) {
    for (const attr of attrs) {
      const { family, magnitude } = sizeFamilyOf(String(item.attrs[attr] ?? ""));
      if (magnitude > 0 && family !== "bare" && family !== UNKNOWN_SIZE_FAMILY) {
        families.add(family);
        break;
      }
    }
  }
  return families;
};

/** Attributes that summarise pack size: the item master's size tier, and any attribute whose
 *  few values split the wall's packs (one unit) into size ranges that never overlap
 *  ("Small pack" 2-12 rolls, "Large pack" 18-48), whatever the merchant calls the column. */
export const packSizeTierAttrs = (items: readonly StudioItem[]): ReadonlySet<string> => {
  const attrs = new Set(items.flatMap((item) => Object.keys(item.attrs)));
  const tiers = new Set<string>(attrs.has(SIZE_TIER_ATTR) ? [SIZE_TIER_ATTR] : []);
  // Pack sizes in different unit families (counts, volumes, weights) do not rank against each
  // other, also where the size is a bare number and the unit lives in another column.
  if (unitFamiliesOf(items).size > 1) return tiers;
  for (const attr of attrs) {
    if (attr === SIZE_ATTR || tiers.has(attr)) continue;
    const spans = new Map<string, [number, number]>();
    const families = new Set<string>();
    for (const item of items) {
      const value = String(item.attrs[attr] ?? "");
      if (isPlaceholderValue(value)) continue;
      const measured = measuredSizeOf(item);
      // A tier splits every pack it tags: one unsized pack, or sizes in two unit families, and
      // the column is something else.
      if (measured !== null) families.add(measured.family);
      if (measured === null || families.size > 1) {
        spans.clear();
        break;
      }
      const size = measured.magnitude;
      const span = spans.get(value) ?? [size, size];
      spans.set(value, [Math.min(span[0], size), Math.max(span[1], size)]);
    }
    // A tier column has a few values (at most as many as `SIZE_TIER_ORDER`), not one per pack.
    const ordered = [...spans.values()].sort((a, b) => a[0] - b[0]);
    const few = ordered.length >= 2 && ordered.length <= SIZE_TIER_ORDER.length;
    if (few && ordered.every((span, i) => i === 0 || ordered[i - 1][1] < span[0])) tiers.add(attr);
  }
  return tiers;
};
